import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import DynamicMenuRender from './dynamic-menu/DynamicMenuRender'
import {
  CustomMenuItemType,
  NO_NAVIGATE_KEY,
} from './dynamic-menu/custom-menu-items-type'
import getAllMenuNestedKeys from './dynamic-menu/helper/get-all-menu-nested-keys'
import { LIST_MENU_DOCUMENT } from '../../document/constants/list-menu.document'
import { DOCUMENT_SHORT_PREFIX_URL } from '../../document/constants/config.document'
import { DMS_SHORT_PREFIX_URL } from '../../tai-lieu/constants/config.dms'
import { LIST_MENU_DOCUMENT_HRM } from '../../tai-lieu/constants/list-menu.dms'
import useCheckRole from '../../list-role/hooks/use-check-role'
import {
  QUAN_LY_GIAO_VIEC_PREFIX_URL,
  QUAN_LY_GIAO_VIEC_SHORT_PREFIX_URL,
} from '../../quan-ly-giao-viec/constants/config.quan-ly-giao-viec'
import {
  PROJECT_DEPARTMENT_PREFIX_URL,
  PROJECT_GENERAL_PREFIX_URL,
} from '../../quan-ly-giao-viec/modules/project/constants/config.project'

import useControlLeftMenuProjectItems from '../../quan-ly-giao-viec/modules/project/hooks/use-control-left-menu-project-items'
import { PHAN_BO_TRANG_THIET_BI_SHORT_PREFIX_URL } from '../../phan-bo-trang-thiet-bi/constants/config.phan-bo-trang-thiet-bi'
import {
  LIST_MENU_CAP_PHAT_ADMIN,
  LIST_MENU_CAP_PHAT_EMPLOYEE,
} from '../../phan-bo-trang-thiet-bi/constants/list-menu-phan-bo-trang-thiet-bi'
import { DAT_XE_SHORT_PREFIX_URL } from '../../dat-xe/constants/config.dat-xe'
import {
  LIST_MENU_DAT_XE_ADMIN,
  LIST_MENU_DAT_XE_EMPLOYEE,
} from '../../dat-xe/constants/list-menu.dat-xe'
import {
  LIST_MENU_DAT_PHONG_HOP_ADMIN,
  LIST_MENU_DAT_PHONG_HOP_EMPLOYEE,
} from '../../dat-phong-hop/constants/list-menu.dat-phong-hop'
import { DAT_PHONG_HOP_SHORT_PREFIX_URL } from '../../dat-phong-hop/constants/config.dat-phong-hop'

// Các path chung
const generalPaths = [
  DOCUMENT_SHORT_PREFIX_URL,
  QUAN_LY_GIAO_VIEC_SHORT_PREFIX_URL,
  DMS_SHORT_PREFIX_URL,
  PHAN_BO_TRANG_THIET_BI_SHORT_PREFIX_URL,
  DAT_XE_SHORT_PREFIX_URL,
  DAT_PHONG_HOP_SHORT_PREFIX_URL,
]

const LeftMenuHRMLayout = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const projectId = (() => {
    if (pathname.includes(`${PROJECT_DEPARTMENT_PREFIX_URL}`))
      return pathname
        .split(`${PROJECT_DEPARTMENT_PREFIX_URL}/`)[1]
        ?.split('/')[0]

    if (pathname.includes(`${PROJECT_GENERAL_PREFIX_URL}`))
      return pathname.split(`${PROJECT_GENERAL_PREFIX_URL}/`)[1]?.split('/')[0]

    return null
  })()

  const [selectedKey, setSelectedKey] = useState<string>('')
  const [expandKeys, setExpandKeys] = useState<string[]>([])

  const { handleCheckRole: checkAdminGiaoViec, result: isAdminGiaoViec } =
    useCheckRole({
      code: 'admin-giao-viec',
      customRequestHandler: true,
    })

  const { handleCheckRole: checkAdminPhongHop, result: isAdminPhongHop } =
    useCheckRole({
      code: 'admin-phong-hop',
      customRequestHandler: true,
    })

  const { handleCheckRole: checkAdminXe, result: isAdminXe } = useCheckRole({
    code: 'admin-xe',
    customRequestHandler: true,
  })

  const { handleCheckRole: checkAdminCapPhat, result: isAdminCapPhat } =
    useCheckRole({
      code: 'admin-cap-phat-trang-thiet-bi',
      customRequestHandler: true,
    })

  const isInQLGiaoViec = pathname.includes(QUAN_LY_GIAO_VIEC_PREFIX_URL)

  const { LIST_MENU_GIAO_VIEC } = useControlLeftMenuProjectItems({
    isAdminGiaoViec: isAdminGiaoViec || false,
    isInQLGiaoViec,
    projectId,
    pathname,
  })

  const LIST_MENU_DAT_PHONG_HOP = useMemo(
    () =>
      isAdminPhongHop
        ? LIST_MENU_DAT_PHONG_HOP_ADMIN
        : LIST_MENU_DAT_PHONG_HOP_EMPLOYEE,
    [isAdminPhongHop]
  )

  const LIST_MENU_DAT_XE = useMemo(
    () => (isAdminXe ? LIST_MENU_DAT_XE_ADMIN : LIST_MENU_DAT_XE_EMPLOYEE),
    [isAdminXe]
  )

  const LIST_MENU_CAP_PHAT = useMemo(
    () =>
      isAdminCapPhat ? LIST_MENU_CAP_PHAT_ADMIN : LIST_MENU_CAP_PHAT_EMPLOYEE,
    [isAdminCapPhat]
  )

  // Các item của menu chính
  const menuItems: CustomMenuItemType<'dynamic'>['children'] = useMemo(
    () => [
      LIST_MENU_DOCUMENT,
      LIST_MENU_GIAO_VIEC,
      LIST_MENU_DOCUMENT_HRM,
      LIST_MENU_DAT_PHONG_HOP,
      LIST_MENU_DAT_XE,
      LIST_MENU_CAP_PHAT,
    ],
    [
      LIST_MENU_GIAO_VIEC,
      LIST_MENU_DAT_PHONG_HOP,
      LIST_MENU_DAT_XE,
      LIST_MENU_CAP_PHAT,
    ]
  )

  const nestedKeysMap = useMemo(
    () => getAllMenuNestedKeys(menuItems),
    [menuItems]
  )

  // Tự động sync menu với URL
  const handleSyncMenu = () => {
    if (!pathname) return

    const allPossiblePath = Array.from(nestedKeysMap.keys())
      // Loại các trường hợp là path chung
      .filter((key) => !generalPaths.some((path) => path === key))
      .map((key) => key)

    // Tìm tất cả path có sự tương tự
    const similarPaths = allPossiblePath.filter((path) =>
      pathname.includes(path)
    )

    // Dùng path giống dài nhất làm path hiện tại
    const currentPath = similarPaths.reduce(
      (prev, curPath) => (curPath.length > prev.length ? curPath : prev),
      ''
    )

    // Nếu có thì mới set để tránh trường hợp không tìm được do menu bố trí chưa hợp lý
    if (currentPath) {
      setSelectedKey(currentPath)

      // Tìm và expand các parent
      const allParents = nestedKeysMap.get(currentPath)?.parentKeys
      if (allParents && allParents.length >= 1) {
        setExpandKeys((prev) => [...prev, ...allParents])
      }
      return
    }

    // Nếu không tìm thấy thì tìm trong path chung để select parent module tương ứng
    // Thường sẽ không cần tới logic này nếu bố trí menu hợp lý

    // Tìm tất cả module path có sự tương tự
    const similarModulePaths = generalPaths.filter((path) =>
      pathname.includes(path)
    )
    // Các parent module key
    const parentKeys = menuItems.map(({ key }) => key)
    // Các parent key giống nhất
    const similarParentKeys = parentKeys.filter((parentKey) =>
      similarModulePaths.some((path) => parentKey.includes(path))
    )
    // Dùng parentKey giống dài nhất làm path hiện tại
    const currentModulePath = similarParentKeys.reduce(
      (prev, curPath) => (curPath.length > prev.length ? curPath : prev),
      ''
    )
    if (currentModulePath) setSelectedKey(currentModulePath)
  }

  useEffect(() => {
    handleSyncMenu()
  }, [pathname, nestedKeysMap])

  useEffect(() => {
    checkAdminGiaoViec()
    checkAdminPhongHop()
    checkAdminXe()
    checkAdminCapPhat()
  }, [])

  return (
    <div style={{ minHeight: '100vh', position: 'sticky', top: 0 }}>
      <DynamicMenuRender
        item={{
          key: 'hrm-layout',
          type: 'dynamic',
          children: menuItems,
        }}
        menuProps={{
          selectedKeys: [selectedKey],
          onClick: ({ key }) => {
            if (key.includes(NO_NAVIGATE_KEY)) return
            navigate(key)
          },
          openKeys: expandKeys,
          onOpenChange: (openKeys) => setExpandKeys(openKeys),
          style: { maxHeight: '78vh', overflowY: 'auto' },
        }}
        onSelectMenuSearchItem={(key) => navigate(key)}
      />
    </div>
  )
}

export default LeftMenuHRMLayout
